/**
 * Small Multiples Chart Component
 *
 * Displays side-by-side mini score trajectory charts with identical scales
 * following Tufte's small multiples pattern for fund comparison.
 */

import { useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import type { Fund, FundRating, RankHistoryEntry } from "@curie/shared";

// ============================================================================
// Types
// ============================================================================

export interface SmallMultiplesChartProps {
  /** Array of funds to compare (2-4 funds recommended) */
  funds: Fund[];
  /** Rank history for each fund, keyed by ISIN */
  rankHistory?: Record<string, RankHistoryEntry[]>;
  /** Additional CSS classes */
  className?: string;
}

interface TrajectoryDataPoint {
  label: string;
  score: number;
  rank: number;
  totalFunds: number;
}

// ============================================================================
// Constants
// ============================================================================

/**
 * Mini chart dimensions (compact for small multiples)
 */
const MINI_CHART_CONFIG = {
  width: 200,
  height: 80,
  paddingX: 16,
  paddingY: 12,
  strokeWidth: 2,
  dotRadius: 3,
} as const;

/**
 * Rating colors for chart lines (matching ScoreTrajectoryChart)
 */
const RATING_COLORS: Record<FundRating, { stroke: string; fill: string }> = {
  "in-form": {
    stroke: "#10b981", // emerald-500
    fill: "#10b981",
  },
  "on-track": {
    stroke: "#0ea5e9", // sky-500
    fill: "#0ea5e9",
  },
  "off-track": {
    stroke: "#f59e0b", // amber-500
    fill: "#f59e0b",
  },
  "out-of-form": {
    stroke: "#f43f5e", // rose-500
    fill: "#f43f5e",
  },
};

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Convert rank to percentile score (inverted so lower rank = higher score)
 */
function rankToScore(rank: number, totalFunds: number): number {
  if (totalFunds === 0) return 50;
  const percentile = (rank / totalFunds) * 100;
  return 100 - percentile;
}

/**
 * Generate mock rank history for a fund based on current rank
 * Creates 6 months of data with slight variations
 */
function generateMockRankHistory(fund: Fund): RankHistoryEntry[] {
  const now = new Date();
  const history: RankHistoryEntry[] = [];

  // Create realistic variation around current rank
  const baseRank = fund.rank;
  const totalFunds = fund.totalFundsInCategory;
  const maxVariation = Math.min(Math.floor(totalFunds * 0.15), 10);

  for (let i = 5; i >= 0; i--) {
    const date = new Date(now);
    date.setMonth(date.getMonth() - i);

    // Add variation that trends towards current rank
    const progress = (5 - i) / 5;
    const randomFactor = (Math.random() - 0.5) * maxVariation;
    const trendFactor = progress * 0.7;
    const variation = Math.round(randomFactor * (1 - trendFactor));

    const rank = Math.max(1, Math.min(totalFunds, baseRank + variation));

    history.push({
      date: date.toISOString().split("T")[0] ?? "",
      rank,
      totalFunds,
      rating: fund.rating,
    });
  }

  // Ensure last entry matches current rank
  if (history[5]) {
    history[5].rank = baseRank;
  }

  return history;
}

/**
 * Format month from ISO date string
 */
function formatMonth(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-US", { month: "short" });
}

/**
 * Process rank history into chart data points
 */
function processRankHistory(rankHistory: RankHistoryEntry[]): TrajectoryDataPoint[] {
  const sorted = [...rankHistory].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
  );
  const recent = sorted.slice(-6);

  return recent.map((entry) => ({
    label: formatMonth(entry.date),
    score: rankToScore(entry.rank, entry.totalFunds),
    rank: entry.rank,
    totalFunds: entry.totalFunds,
  }));
}

/**
 * Calculate global min/max scores across all funds for consistent Y-axis scaling
 */
function calculateGlobalScaleRange(
  allDataPoints: TrajectoryDataPoint[][]
): { min: number; max: number } {
  const allScores = allDataPoints.flat().map((p) => p.score);

  if (allScores.length === 0) {
    return { min: 0, max: 100 };
  }

  const minScore = Math.min(...allScores);
  const maxScore = Math.max(...allScores);
  const scoreRange = maxScore - minScore || 20;

  // Add 10% padding to score range
  return {
    min: Math.max(0, minScore - scoreRange * 0.1),
    max: Math.min(100, maxScore + scoreRange * 0.1),
  };
}

/**
 * Generate SVG path for line chart with fixed Y-axis scale
 */
function generateLinePath(
  points: TrajectoryDataPoint[],
  chartWidth: number,
  chartHeight: number,
  scaleMin: number,
  scaleMax: number
): string {
  if (points.length === 0) return "";

  const { paddingX, paddingY } = MINI_CHART_CONFIG;
  const plotWidth = chartWidth - paddingX * 2;
  const plotHeight = chartHeight - paddingY * 2;
  const scaleRange = scaleMax - scaleMin;

  const pathPoints = points.map((point, index) => {
    const x = paddingX + (index / Math.max(1, points.length - 1)) * plotWidth;
    const y = paddingY + ((scaleMax - point.score) / scaleRange) * plotHeight;
    return { x, y };
  });

  // Create smooth bezier curve
  let path = `M ${pathPoints[0]?.x ?? 0},${pathPoints[0]?.y ?? 0}`;

  for (let i = 1; i < pathPoints.length; i++) {
    const prev = pathPoints[i - 1];
    const curr = pathPoints[i];
    if (!prev || !curr) continue;

    const tension = 0.3;
    const prevPrev = pathPoints[i - 2] || prev;
    const next = pathPoints[i + 1] || curr;

    const cp1x = prev.x + (curr.x - prevPrev.x) * tension;
    const cp1y = prev.y + (curr.y - prevPrev.y) * tension;
    const cp2x = curr.x - (next.x - prev.x) * tension;
    const cp2y = curr.y - (next.y - prev.y) * tension;

    path += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${curr.x},${curr.y}`;
  }

  return path;
}

/**
 * Generate SVG path for area fill under the line
 */
function generateAreaPath(
  points: TrajectoryDataPoint[],
  chartWidth: number,
  chartHeight: number,
  scaleMin: number,
  scaleMax: number
): string {
  if (points.length === 0) return "";

  const linePath = generateLinePath(points, chartWidth, chartHeight, scaleMin, scaleMax);
  const { paddingX, paddingY } = MINI_CHART_CONFIG;
  const plotWidth = chartWidth - paddingX * 2;
  const plotHeight = chartHeight - paddingY * 2;

  const lastX = paddingX + plotWidth;
  const bottomY = paddingY + plotHeight;
  const firstX = paddingX;

  return `${linePath} L ${lastX},${bottomY} L ${firstX},${bottomY} Z`;
}

/**
 * Truncate fund name for display
 */
function truncateName(name: string, maxLength: number = 24): string {
  if (name.length <= maxLength) return name;
  return name.slice(0, maxLength - 1).trim() + "...";
}

// ============================================================================
// Sub-components
// ============================================================================

/**
 * Individual mini chart for a single fund
 */
function MiniChart({
  fund,
  dataPoints,
  scaleMin,
  scaleMax,
  chartId,
}: {
  fund: Fund;
  dataPoints: TrajectoryDataPoint[];
  scaleMin: number;
  scaleMax: number;
  chartId: string;
}) {
  const { width, height, paddingX, paddingY, strokeWidth, dotRadius } = MINI_CHART_CONFIG;
  const colors = RATING_COLORS[fund.rating];
  const scaleRange = scaleMax - scaleMin;
  const plotWidth = width - paddingX * 2;
  const plotHeight = height - paddingY * 2;

  if (dataPoints.length < 2) {
    return (
      <div className="flex flex-col items-center">
        <div className="text-xs font-medium text-muted-foreground mb-1 truncate max-w-[200px]">
          {truncateName(fund.amcName)}
        </div>
        <div
          className="flex items-center justify-center bg-muted/50 rounded-lg"
          style={{ width, height }}
        >
          <span className="text-xs text-muted-foreground">No data</span>
        </div>
      </div>
    );
  }

  const linePath = generateLinePath(dataPoints, width, height, scaleMin, scaleMax);
  const areaPath = generateAreaPath(dataPoints, width, height, scaleMin, scaleMax);

  // Calculate dot positions
  const dotPositions = dataPoints.map((point, index) => ({
    x: paddingX + (index / Math.max(1, dataPoints.length - 1)) * plotWidth,
    y: paddingY + ((scaleMax - point.score) / scaleRange) * plotHeight,
    rank: point.rank,
    label: point.label,
  }));

  const startPoint = dataPoints[0];
  const endPoint = dataPoints[dataPoints.length - 1];
  const rankChange = startPoint && endPoint ? startPoint.rank - endPoint.rank : 0;

  return (
    <div className="flex flex-col items-center">
      {/* Fund AMC name */}
      <div
        className="text-xs font-medium text-muted-foreground mb-1 truncate max-w-[200px] text-center"
        title={fund.amcName}
      >
        {truncateName(fund.amcName)}
      </div>

      {/* Mini sparkline */}
      <svg
        width={width}
        height={height}
        className="overflow-visible"
        viewBox={`0 0 ${width} ${height}`}
        aria-label={`Rank trajectory for ${fund.name}`}
      >
        {/* Gradient definition */}
        <defs>
          <linearGradient id={`mini-gradient-${chartId}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={colors.fill} stopOpacity="0.25" />
            <stop offset="100%" stopColor={colors.fill} stopOpacity="0.05" />
          </linearGradient>
        </defs>

        {/* Baseline */}
        <line
          x1={paddingX}
          y1={height - paddingY}
          x2={width - paddingX}
          y2={height - paddingY}
          stroke="currentColor"
          strokeOpacity={0.1}
        />

        {/* Area fill */}
        <path d={areaPath} fill={`url(#mini-gradient-${chartId})`} />

        {/* Main line */}
        <path
          d={linePath}
          fill="none"
          stroke={colors.stroke}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        />

        {/* Start and end dots only */}
        {dotPositions.length > 0 && (
          <>
            {/* Start dot */}
            <circle
              cx={dotPositions[0]?.x}
              cy={dotPositions[0]?.y}
              r={dotRadius}
              fill="white"
              stroke={colors.stroke}
              strokeWidth={1.5}
            />
            {/* End dot (filled) */}
            <circle
              cx={dotPositions[dotPositions.length - 1]?.x}
              cy={dotPositions[dotPositions.length - 1]?.y}
              r={dotRadius}
              fill={colors.fill}
              stroke={colors.stroke}
              strokeWidth={1.5}
            />
          </>
        )}
      </svg>

      {/* Rank display */}
      <div className="flex items-center justify-between w-full px-2 mt-1">
        <span className="text-xs text-muted-foreground">#{startPoint?.rank ?? "-"}</span>
        <span
          className={cn(
            "text-xs font-medium",
            rankChange > 0
              ? "text-emerald-600"
              : rankChange < 0
                ? "text-rose-600"
                : "text-muted-foreground"
          )}
        >
          {rankChange > 0 ? "+" : ""}
          {rankChange !== 0 ? rankChange : "-"}
        </span>
        <span
          className={cn("text-xs font-semibold")}
          style={{ color: colors.stroke }}
        >
          #{endPoint?.rank ?? "-"}
        </span>
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

/**
 * Small Multiples Chart
 *
 * Displays 2-4 mini score trajectory charts side by side with identical Y-axis scales,
 * enabling direct visual comparison of fund performance trajectories.
 *
 * @example
 * ```tsx
 * <SmallMultiplesChart
 *   funds={[fund1, fund2, fund3]}
 *   className="mt-4"
 * />
 * ```
 */
export function SmallMultiplesChart({ funds, rankHistory, className }: SmallMultiplesChartProps) {
  // Process rank history data for each fund
  // Uses real rank history from API when available, falls back to mock data
  const fundsData = useMemo(
    () =>
      funds.map((fund) => {
        // Use real rank history from API if available, otherwise generate mock
        const fundRankHistory = rankHistory?.[fund.isin];
        const history = fundRankHistory && fundRankHistory.length > 0
          ? fundRankHistory
          : generateMockRankHistory(fund);
        const dataPoints = processRankHistory(history);
        return { fund, dataPoints };
      }),
    [funds, rankHistory]
  );

  // Calculate global scale for consistent Y-axis across all charts
  const { min: scaleMin, max: scaleMax } = useMemo(
    () => calculateGlobalScaleRange(fundsData.map((fd) => fd.dataPoints)),
    [fundsData]
  );

  if (funds.length === 0) {
    return (
      <Card className={cn(className)}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold">Rank Trajectory Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-24 text-sm text-muted-foreground">
            Add funds to compare their rank trajectories
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-semibold">Rank Trajectory Comparison</CardTitle>
          <span className="text-xs text-muted-foreground">Last 6 months</span>
        </div>
      </CardHeader>
      <CardContent>
        {/* Responsive grid: stack on mobile, side-by-side on larger screens */}
        <div
          className={cn(
            "grid gap-4",
            funds.length === 2 && "grid-cols-1 sm:grid-cols-2",
            funds.length === 3 && "grid-cols-1 sm:grid-cols-3",
            funds.length >= 4 && "grid-cols-2 sm:grid-cols-4"
          )}
        >
          {fundsData.map(({ fund, dataPoints }, index) => (
            <MiniChart
              key={fund.isin}
              fund={fund}
              dataPoints={dataPoints}
              scaleMin={scaleMin}
              scaleMax={scaleMax}
              chartId={`${fund.isin}-${index}`}
            />
          ))}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center gap-6 mt-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full border-2 border-current bg-white" />
            <span>Start Rank</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-full bg-current" />
            <span>Current Rank</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ============================================================================
// Skeleton Loader
// ============================================================================

/**
 * Skeleton loader for SmallMultiplesChart
 */
export function SmallMultiplesChartSkeleton({
  count = 3,
  className,
}: {
  count?: number;
  className?: string;
}) {
  return (
    <Card className={cn(className)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-20" />
        </div>
      </CardHeader>
      <CardContent>
        <div
          className={cn(
            "grid gap-4",
            count === 2 && "grid-cols-1 sm:grid-cols-2",
            count === 3 && "grid-cols-1 sm:grid-cols-3",
            count >= 4 && "grid-cols-2 sm:grid-cols-4"
          )}
        >
          {Array.from({ length: count }).map((_, index) => (
            <div key={index} className="flex flex-col items-center">
              <Skeleton className="h-3 w-32 mb-1" />
              <Skeleton
                className="rounded-lg"
                style={{ width: MINI_CHART_CONFIG.width, height: MINI_CHART_CONFIG.height }}
              />
              <div className="flex items-center justify-between w-full px-2 mt-1">
                <Skeleton className="h-3 w-6" />
                <Skeleton className="h-3 w-6" />
                <Skeleton className="h-3 w-6" />
              </div>
            </div>
          ))}
        </div>

        {/* Legend skeleton */}
        <div className="flex items-center justify-center gap-6 mt-4">
          <Skeleton className="h-3 w-20" />
          <Skeleton className="h-3 w-24" />
        </div>
      </CardContent>
    </Card>
  );
}

export default SmallMultiplesChart;
